import { useSDK } from "~hooks";
import { BroadcastCommunicationType, Order } from "~graphql/sdk";
import { useEffect } from "react";
import {
  broadcastParamsFromReportingString,
  BroadcastReportingTracking,
  BroadcastReportingTrackingRaw,
  getBroadcastParamsFromQuery,
} from "~hooks/broadcastHelper";
import { useRouter } from "next/router";
import { addDays } from "date-fns";

const BroadcastReportingData = "BroadcastReportingData";
type BroadcastReportingDataType = BroadcastReportingTrackingRaw & {
  expiresAt: number;
};
const getBroadcastReportingTrackingCode = (): BroadcastReportingTracking | void => {
  const reportingData = localStorage?.getItem(BroadcastReportingData);
  const data: BroadcastReportingDataType | null = reportingData
    ? JSON.parse(reportingData)
    : null;

  if (data && !data.communicationType) {
    // Add type in case data was stored before type was added with SMS.
    data.communicationType = BroadcastCommunicationType.Email;
  }

  if (data) {
    if (Date.now() > data.expiresAt) {
      localStorage?.removeItem(BroadcastReportingData);
    } else {
      return broadcastParamsFromReportingString(
        data.communicationType,
        data.rawData
      );
    }
  }
};

const setReportingData = (
  communicationType: BroadcastCommunicationType,
  rawData: string
) => {
  localStorage?.setItem(
    BroadcastReportingData,
    JSON.stringify({
      communicationType,
      rawData,
      expiresAt: addDays(new Date(), 7).getTime(),
    })
  );
};

export const useCampaignReportingOpen = (trigger: boolean) => {
  const router = useRouter();
  const sdk = useSDK();
  const { query } = router;
  const broadcast = getBroadcastParamsFromQuery(query);
  if (!broadcast) {
    return;
  }

  const {
    communicationType,
    broadcastId,
    userId,
    waitlistUserId,
    membershipId,
    eventId,
    rawData,
  } = broadcast;

  useEffect(() => {
    if (!trigger || !broadcastId) return;

    if (userId || waitlistUserId) {
      const doAsyncReporting = async () => {
        try {
          await sdk.broadcastReportingLinkOpened({
            input: {
              communicationType,
              broadcastId,
              membershipId,
              eventId,
              userId,
              waitlistUserId,
            },
          });
        } catch (error) {
          // Handle errors
          console.error("Error saving data:", error);
        }
      };
      void doAsyncReporting();
    }

    setReportingData(communicationType, rawData);
  }, [trigger]);
};

export const useCampaignReportingForOrder = (order: Order) => {
  const trackingCode = getBroadcastReportingTrackingCode();
  const sdk = useSDK();
  if (!trackingCode) {
    return;
  }

  const {
    communicationType,
    broadcastId,
    userId,
    waitlistUserId,
    eventId,
    membershipId,
  } = trackingCode;

  useEffect(() => {
    void sdk.broadcastReportingOrderCreated({
      input: {
        communicationType,
        broadcastId,
        eventId,
        membershipId,
        userId: userId ?? order.user?.id, // Need to use order user id for SMS broadcasts
        waitlistUserId,
        orderId: order.id,
      },
    });
  }, [order.id]);
};
